import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DemoService } from 'src/app/services/common/demo.service';
import { DemoFlowService } from 'src/app/services/common/demo-flow.service';

/** Cosa raccontare di una pagina, la prima volta che ci si arriva. */
interface Spiegazione { titolo: string; testo: string; }

/**
 * In demo, la prima volta che il visitatore apre una pagina compare un
 * cartellino che dice in due righe cosa può fare lì.
 *
 * È il seguito della guida di benvenuto: quella racconta il prodotto, questi
 * accompagnano chi sta già girando per l'app. Uno per pagina, una volta sola
 * per visita, e mai a chi ha un account.
 */
@Component({
  selector: 'app-demo-spiegazione',
  templateUrl: './demo-spiegazione.component.html',
  styleUrls: ['./demo-spiegazione.component.scss']
})
export class DemoSpiegazioneComponent implements OnInit, OnDestroy {

  /** Le pagine già spiegate in questa visita. Statico come DemoTourComponent.giaVisto. */
  private static viste: {[chiave: string]: boolean} = {};

  visibile = false;
  chiave: string;
  private sottoscrizioni: Subscription[] = [];

  /** Le pagine della barra, con la spiegazione che compare arrivandoci. */
  readonly spiegazioni: {[chiave: string]: Spiegazione} = {
    mappa: {
      titolo: 'Qui vedi dove sono i mezzi, adesso',
      testo: 'Tocca un veicolo: posizione, velocità e da quanto è fermo. Si aggiorna da solo.'
    },
    percorsi: {
      titolo: 'Dove sono stati, giorno per giorno',
      testo: 'Scegli il mezzo e il giorno: compare il percorso con tutte le soste.'
    },
    allarmi: {
      titolo: 'Ti avvisiamo noi quando succede qualcosa',
      testo: 'Scegli un veicolo e imposta una regola: zona, orario o velocità. L\'avviso arriva sul telefono.'
    }
    // Il menu non ha cartellino: in demo si apre sull'invito a registrarsi,
    // che dice già tutto, e il cartellino ci finirebbe sopra.
  };

  constructor(
    private cd: ChangeDetectorRef,
    private router: Router,
    private demoFlow: DemoFlowService
  ){}

  ngOnInit(){
    if(!DemoService.active) return;

    this.valuta(this.router.url);
    this.sottoscrizioni.push(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd))
        .subscribe((e: any) => this.valuta(e.urlAfterRedirects || e.url)),
      // Finché la guida di benvenuto è a schermo il cartellino aspetta il suo turno.
      this.demoFlow.guidaDiBenvenutoChiusa$.subscribe(() => this.valuta(this.router.url)),
      DemoService.chiusa.subscribe(() => this.chiudi())
    );
  }

  ngOnDestroy(){
    this.sottoscrizioni.forEach(s => s.unsubscribe());
  }

  get spiegazione(): Spiegazione { return this.spiegazioni[this.chiave]; }

  /** "2 di 4 pagine viste": dà la misura di quanto resta da guardare. */
  get avanzamento(): string {
    const totale: number = Object.keys(this.spiegazioni).length;
    const viste: number = Object.keys(DemoSpiegazioneComponent.viste).length;
    return viste + ' di ' + totale + ' pagine viste';
  }

  /** Chiude il cartellino: quella pagina non lo rimostra più. */
  chiudi(){
    this.visibile = false;
    this.cd.detectChanges();
  }

  /** Decide se mostrare il cartellino per l'indirizzo in cui ci troviamo. */
  private valuta(url: string){
    // Solo nella demo pubblica. L'ascolto dei cambi pagina parte quando la demo
    // è attiva, ma la demo può finire dopo (chi si registra e rientra col suo
    // account): da lì in poi nessun cartellino, neanche arrivando sulla mappa.
    if(!DemoService.active){
      if(this.visibile) this.chiudi();
      return;
    }
    const chiave: string = DemoSpiegazioneComponent.chiaveDa(url);

    // Una stessa navigazione può annunciarsi più volte (i tab di Ionic ne
    // mandano una per il contenitore e una per la pagina): se il cartellino di
    // questa pagina è già a schermo lo si lascia stare, altrimenti il secondo
    // avviso lo chiuderebbe un istante dopo averlo aperto.
    if(chiave && chiave === this.chiave && this.visibile) return;

    if(!chiave || DemoSpiegazioneComponent.viste[chiave] || this.demoFlow.guidaDiBenvenutoAperta){
      if(this.visibile) this.chiudi();
      return;
    }
    DemoSpiegazioneComponent.viste[chiave] = true;
    this.chiave = chiave;
    this.visibile = true;
    this.cd.detectChanges();
  }

  /** Dall'indirizzo alla pagina: solo mappa, percorsi e allarmi, e solo la loro radice. */
  private static chiaveDa(url: string): string {
    const pulito: string = (url || '').split('?')[0];
    if(pulito.endsWith('/tabs/map')) return 'mappa';
    if(pulito.endsWith('/tabs/history')) return 'percorsi';
    if(pulito.endsWith('/tabs/alarm-cars')) return 'allarmi';
    return null;
  }
}
